import { Injectable } from "@angular/core";
import { isNotNullOrEmpty } from "../../common/functions/is-not-null-or-empty.function";
import { DisclosureValidationStatement } from "../../common/models/disclosures/disclosure-validation-statement.model";
import { RefThirdPartyStatus } from "../../common/models/enums/third-party-chemical-status.enum";
import { DisclosureValidationIdType } from "../models/disclosure-validation-id-type.enum";
import { IngredientService } from '../../common/services/ingredient.service';
import { ColDef, ExcelStyle, GridOptions, GridReadyEvent, IDateFilterParams, IRowNode, ProcessCellForExportParams, ProcessRowParams, RowGroupOpenedEvent, SuppressKeyboardEventParams, ValueFormatterFunc, ValueFormatterParams, ExcelExportParams, GridApi } from "ag-grid-enterprise";

// Free function (not a class method) so errorAppliesToField/getFieldValidationMessage can
// keep being passed around as bare function references (eg. `isValid: this.agGridHelperService.errorAppliesToField`)
// without losing their `this` binding.
function getMatchingValidationStatements(validationStatements: Array<DisclosureValidationStatement>, fieldName: string, idType: DisclosureValidationIdType, itemId: string): Array<DisclosureValidationStatement> {
  const matches: Array<DisclosureValidationStatement> = [];

  for (let x = 0; x < validationStatements.length; x++) {
    let relevantStatement = validationStatements[x];

    let fieldName1 = "";
    let fieldName2 = "";
    let fieldName3 = "";
    let fieldName4 = "";

    if (relevantStatement.field1 != null && relevantStatement.field1 != undefined) {
      fieldName1 = relevantStatement.field1;
    }

    if (relevantStatement.field2 != null && relevantStatement.field2 != undefined) {
      fieldName2 = fieldName2;
    }

    if (relevantStatement.field3 != null && relevantStatement.field3 != undefined) {
      fieldName3 = fieldName3;
    }

    if (relevantStatement.field4 != null && relevantStatement.field4 != undefined) {
      fieldName4 = fieldName4;
    }

    switch (idType) {
      case DisclosureValidationIdType.Purpose:
        if (relevantStatement.purposeId != null && relevantStatement.purposeId != undefined && relevantStatement.purposeId == itemId
          && (fieldName1 == fieldName || fieldName2 == fieldName || fieldName3 == fieldName || fieldName4 == fieldName)) {
          matches.push(relevantStatement);
        }
        break;
      case DisclosureValidationIdType.Ingredient:
        if (relevantStatement.ingredientsId != null && relevantStatement.ingredientsId != undefined && relevantStatement.ingredientsId == itemId
          && (fieldName1 == fieldName || fieldName2 == fieldName || fieldName3 == fieldName || fieldName4 == fieldName)) {
          matches.push(relevantStatement);
        }
        break;
      case DisclosureValidationIdType.WaterSource:
        if (relevantStatement.waterSourceId != null && relevantStatement.waterSourceId != undefined && relevantStatement.waterSourceId == itemId
          && (fieldName1 == fieldName || fieldName2 == fieldName || fieldName3 == fieldName || fieldName4 == fieldName)) {
          matches.push(relevantStatement);
        }
        break;
    }
  }

  return matches;
}

@Injectable({
  providedIn: 'root'
})
export class AGGridHelperService {
      
  constructor(private ingredientService: IngredientService) {
  }

  getDefaultDateFilterParams(): IDateFilterParams {
    return {
      buttons: ['reset'],
      dateFormat: 'mm/dd/yyyy',
      includeTime: false,
      comparator: this.dateFilterComparer
    } as IDateFilterParams;
  }

  getDefaultExcelStyles(): ExcelStyle[]{
    return [
      {
        id: 'body',
        dataType: 'String',
        interior: {
          color: '#dddddd',
          pattern: 'Solid',
        },
        alignment: { horizontal: 'Left' },
      } as ExcelStyle,
      {
        id: 'numberType',
        numberFormat: {
            format: '0',
        },
      } as ExcelStyle,
      {
        id: 'subContentNumberWithNoDecimalsType',
        dataType: 'Number',
        interior: {
          color: '#dddddd',
          pattern: 'Solid',
        },
        numberFormat: {
          format: '0',
        },
        alignment: { horizontal: 'Left' },
      } as ExcelStyle,
      {
        id: 'currencyFormat',
        numberFormat: {
            format: '#,##0.00 €',
        },
      } as ExcelStyle,
      {
        id: 'negativeInBrackets',
        numberFormat: {
            format: '$[blue] #,##0;$ [red](#,##0)',
        },
      } as ExcelStyle,
      {
        id: 'booleanType',
        dataType: 'Boolean',
      } as ExcelStyle,
      {
        id: 'stringType',
        dataType: 'String',
        alignment: { horizontal: 'Left' },
      } as ExcelStyle,
      {
        id: 'dateType',
        dataType: 'DateTime',
      } as ExcelStyle,
      {
        id: 'header',
        interior: {
          color: '#aaaaaa',
          pattern: 'Solid',
        },
        alignment:{ horizontal:'Left'}
      } as ExcelStyle,
    ]
  }

  getDefaultColumnDef(suppressEnterWhileEditing: boolean = false): ColDef {
    let defaultColDef = {
      sortable: true,
      filter: true,
      resizable: true,
      flex: 1,
    } as ColDef;

    if (suppressEnterWhileEditing) {
      //This is to prevent pressing the enter key from ending editing to preserve data reset on cancel
      defaultColDef.suppressKeyboardEvent = (event: SuppressKeyboardEventParams) => {
        let shouldSuppress: boolean = false;

        if (event.event.keyCode == 13)
          shouldSuppress = true;

        return shouldSuppress;
      }
    }

    return defaultColDef;
  }

  errorAppliesToField(validationStatements: Array<DisclosureValidationStatement>, fieldName: string, idType: DisclosureValidationIdType, itemId: string): boolean {
    return getMatchingValidationStatements(validationStatements, fieldName, idType, itemId).length === 0;
  }

  // Companion to errorAppliesToField: same matching rules, but returns the actual message
  // text so invalid grid cells can describe the error rather than only flagging it visually.
  getFieldValidationMessage(validationStatements: Array<DisclosureValidationStatement>, fieldName: string, idType: DisclosureValidationIdType, itemId: string): string {
    return getMatchingValidationStatements(validationStatements, fieldName, idType, itemId)
      .map(statement => statement.message)
      .filter(message => isNotNullOrEmpty(message))
      .join(' ');
  }

  public exportDataToExcel(fileName: string, sheetName: string, gridApi: GridApi, selectedOnly: boolean = false, excelExportParams?: ExcelExportParams, skipPinnedRows: boolean = true): void {
    const params = {
      skipHeader: false,
      allColumns: true,
      fileName: fileName,
      sheetName: sheetName,
      onlySelected: selectedOnly,
      onlySelectedAllPages: selectedOnly,
      excelExportParams: excelExportParams,
      skipPinnedTop: skipPinnedRows,
      skipPinnedBottom: skipPinnedRows,
      getCustomContentBelowRow: excelExportParams?.getCustomContentBelowRow,
      processCellCallback: excelExportParams?.processCellCallback ? excelExportParams?.processCellCallback : (params: ProcessCellForExportParams) => this.processCallback(params)
    } as ExcelExportParams;
    gridApi.exportDataAsExcel(params);
  }

  private processCallback(params: any): string {
    const colDef: ColDef = params.column.getColDef();
    let returnValue = params.value;
    colDef.valueFormatter = colDef.valueFormatter as ValueFormatterFunc
    if (colDef.valueFormatter) {
      const valueFormatterParams: ValueFormatterParams = {
        ...params,
        data: params.node.data,
        node: params.node!,
        colDef: params.column.getColDef()
      };
      returnValue = colDef.valueFormatter(valueFormatterParams);
    }
    return returnValue;
  }

  dateFilterComparer(filterLocalDateAtMidnight: Date, cellValue: string) {
    var filterDate = new Date(filterLocalDateAtMidnight);
    var cellDate = new Date(cellValue);
    
    var filterDateUTC = new Date(Date.UTC(filterDate.getUTCFullYear(), filterDate.getUTCMonth(), filterDate.getUTCDate()));
    var cellDateUTC = new Date(Date.UTC(cellDate.getUTCFullYear(), cellDate.getUTCMonth(), cellDate.getUTCDate()));
    
    if (cellDateUTC < filterDateUTC) {
        return -1;
    } else if (cellDateUTC > filterDateUTC) {
        return 1;
    } else {
        return 0;
    }
  }

  thirdPartyStatusComparer(status1: string, status2: string) {
    let convertedStatus1 = ConvertStatusToRankedVersions(status1);
    let convertedStatus2 = ConvertStatusToRankedVersions(status2);
    if (convertedStatus1 < convertedStatus2) {
      return -1;
    }
    if (convertedStatus1 > convertedStatus2) {
      return 1;
    }
    return 0;


    function ConvertStatusToRankedVersions(status: string): number {
      switch (status) {
        case RefThirdPartyStatus.Pending:
          return 4;
        case RefThirdPartyStatus.Active:
          return 3;
        case RefThirdPartyStatus.Inactive:
          return 2;
        case RefThirdPartyStatus.Deleted:
          return 1;
      }

      return 0;
    }
  }

  public calculateMinMaxGridHeightAsNum(numRows: number, numPinnedRows: number = 1, min: number, max: number, rowHeight: number = 40.5): number {
    if (numRows < min) {
      numRows = min;
    }
    if (numRows > max) {
      numRows = max;
    }
    //50 : header, 47: paging
    let rowHeights = 50 + 47 + ((numRows + numPinnedRows) * rowHeight);
    return rowHeights;
  }

  //used for outer min/max grids
  public calculateMinMaxGridHeight(numRows: number, numPinnedRows: number = 1, min: number, max: number, rowHeight: number = 40.5): string {
    let rowHeights = this.calculateMinMaxGridHeightAsNum(numRows, numPinnedRows, min, max, rowHeight);
    return rowHeights + "px";
  }

  //used for inner/full height grids
  public calculateGridHeight(numRows: number, numPinnedRows: number = 0, appendPX:boolean = true): string | number {

    if (numRows < 1) {
      numRows = 1;
    }

    let rowHeights = 50 + ((numRows + numPinnedRows) * 41);

    return appendPX ? rowHeights + "px" : rowHeights;
  }

  public calculateDetailPanelHeight(numRows: number, numPinnedRows: number = 0): number {
    if (numRows < 1) {
      numRows = 1;
    }
    return (numRows + numPinnedRows + 2.25) * 41;
  }

  public calculateHistoryDetailPanelHeight(numRows: number): number {
    if (numRows === 0) {
      //for "No history data found" line
      numRows = 1;
    }
    //default height: History / Close History row
    const defaultHeight = 35;
    //27: single history row with margin bottom
    const rowHeight = 27;
    //padding for detail cell
    const detailCellPadding = 32;

    return defaultHeight + (numRows * rowHeight) + detailCellPadding;
  }

  public calculateDetailRowHeightWithAdditionalInformation(node: IRowNode): number {
    let numRows = node.data.ingredients ? node.data.ingredients.length : 0;
    let numRowsWithAdditionalInfo = 0;
    if (numRows > 0) {
      numRowsWithAdditionalInfo = node.data.ingredients.filter((x:any) => this.ingredientService.validCASAlternatives.some(y => y.toLowerCase() == x.casNumber.toLowerCase())).length;
    }
    //calc details panel height
    let detailPanelHeight = this.calculateDetailPanelHeight(numRows);
    detailPanelHeight += 80 * numRowsWithAdditionalInfo;

    return detailPanelHeight;
  }

  public calculateExpandedGridHeight(unexpandedGridHeight: string, combinedExpandedRowHeight:number): string {
    let unexpandedGridHeightNum = parseInt(unexpandedGridHeight.slice(0, unexpandedGridHeight.length - 2));
    let combinedGridHeight = combinedExpandedRowHeight + unexpandedGridHeightNum;
    return combinedGridHeight.toString() + "px";
  }

  public formatAPINumber(apiNumber: string): string {
    if (!isNotNullOrEmpty(apiNumber)) {
      return apiNumber;
    }

    const mask = '00-000-00000-00-00';
    let formattedValue = '';
    let index = 0;
    for (let i = 0; i < mask.length; i++) {
      if (mask[i] === '0') {
        if (index < apiNumber.length) {
          formattedValue += apiNumber[index];
          index++;
        } else {
          formattedValue += '0';
        }
      } else {
        formattedValue += mask[i];
      }
    }
    return formattedValue;
  }

  /**
   * Guards against the WCAG "aria-hidden element contains focusable content" violation
   * on ag-grid's tab guard divs by removing aria-hidden, NOT by removing focusability.
   * Safe to call on any ag-grid instance (master-detail or flat).
   * Composes with any existing onGridReady on the provided options.
   */
  public applyTabGuardAriaFix(gridOptions: GridOptions): void {
    const existingOnGridReady = gridOptions.onGridReady;
    gridOptions.onGridReady = (event: GridReadyEvent) => {
      existingOnGridReady?.(event);
      const gridEl = event.api.getGridElement();
      const stripAriaHidden = () => {
        gridEl.querySelectorAll<HTMLElement>('.ag-tab-guard[aria-hidden]').forEach(el => {
          el.removeAttribute('aria-hidden');
        });
      };
      stripAriaHidden();
      //re-strip if anything re-applies aria-hidden as focus moves in and out of the grid
      const observer = new MutationObserver(stripAriaHidden);
      gridEl.querySelectorAll<HTMLElement>('.ag-tab-guard').forEach(el => {
        observer.observe(el, { attributes: true, attributeFilter: ['aria-hidden'] });
      });
    };
  }

  /**
   * Removes aria-expanded from row elements to fix WCAG violation where ag-grid sets
   * aria-expanded on role="row" inside role="grid" (only valid in role="treegrid").
   * Also calls applyTabGuardAriaFix.
   * Composes with any existing processRowPostCreate/onRowGroupOpened/onGridReady on the provided options.
   */
  public applyMasterDetailAriaFix(gridOptions: GridOptions): void {
    this.applyTabGuardAriaFix(gridOptions);

    const existingProcessRow = gridOptions.processRowPostCreate;
    const existingRowGroupOpened = gridOptions.onRowGroupOpened;
    const existingOnGridReady = gridOptions.onGridReady;

    gridOptions.processRowPostCreate = (params: ProcessRowParams) => {
      existingProcessRow?.(params);
      params.eRow.removeAttribute('aria-expanded');
    };

    gridOptions.onRowGroupOpened = (event: RowGroupOpenedEvent) => {
      existingRowGroupOpened?.(event);
      requestAnimationFrame(() => {
        const gridEl = event.api.getGridElement();
        // Strip aria-expanded from the master row AND the detail row it expands into.
        // processRowPostCreate only runs for the center row container, so it never sees
        // full-width detail rows, and the detail row's id is prefixed with "detail_".
        gridEl.querySelector<HTMLElement>(`[row-id="${event.node.id}"]`)
          ?.removeAttribute('aria-expanded');
        gridEl.querySelector<HTMLElement>(`[row-id="detail_${event.node.id}"]`)
          ?.removeAttribute('aria-expanded');
        if (event.node.expanded) {
          // ag-grid drops a detail renderer straight into the detail row, and role="row" only
          // permits cell/gridcell children, so the panel's focusable content gets flagged as an
          // invalid child of the row. Roling the row's immediate children as gridcell fixes it;
          // gridcell has no required-children constraint, so any content inside is valid.
          // onRowGroupOpened + rAF guarantees Angular has rendered the detail component.
          // NOTE: ag-grid 36 has no .ag-full-width-cell element - the renderer's own root is
          // the row's only child - so this targets that child directly.
          gridEl.querySelectorAll<HTMLElement>('.ag-full-width-row > *:not([role])')
            .forEach(el => el.setAttribute('role', 'gridcell'));
        }
      });
    };

    gridOptions.onGridReady = (event: GridReadyEvent) => {
      existingOnGridReady?.(event);
      const gridEl = event.api.getGridElement();

      // The overlay wrapper is non-focusable: aria-hidden is safe and removes it
      // from role="grid"'s required-children check without affecting interactivity.
      gridEl.querySelector<HTMLElement>('.ag-overlay-wrapper')
        ?.setAttribute('aria-hidden', 'true');

      // The live region announces grid state but is an invalid non-row child of
      // role="grid". Hiding it accepts a tradeoff: ag-grid's screen reader
      // announcements (sort state, row count, etc.) will not be spoken.
      gridEl.querySelector<HTMLElement>('.ag-aria-description-container')
        ?.setAttribute('aria-hidden', 'true');

      // When this grid is the detail renderer inside another grid (ingredient-edit),
      // non-grid siblings in the same component (tooltip, export buttons, etc.) appear
      // as invalid ARIA children of the enclosing role="row" detail row. Hide those
      // sections and make their interactive elements non-focusable so they don't
      // also trigger the "aria-hidden + focusable" rule.
      const hostEl = gridEl.closest<HTMLElement>('ingredient-edit');
      if (hostEl) {
        hostEl.querySelectorAll<HTMLElement>('.tooltip-wrapper, .export-button-container')
          .forEach(el => {
            el.setAttribute('aria-hidden', 'true');
            el.querySelectorAll<HTMLElement>('button, a, input, select, textarea, [tabindex]')
              .forEach(child => child.setAttribute('tabindex', '-1'));
          });
      }
    };
  }

  public gridInfoHelpText: string = `
    <ul style="list-style: none;">
          <li>Each column in the grid below can be sorted by clicking on the column header.</li> <br/>
          <li>You can filter on any column by first clicking on this icon <img src="../assets/svg/ag-grid-icons/filter.svg" width="40" height="40" alt="Filter icon">  you can then choose and enter filtering rules to search for text, values or dates. <br/> You can add multiple filters on each column for detailed searches in the grid.</li><br/>
          <li>You can add additional columns to the grid by clicking the menu icon <img src="../assets/svg/ag-grid-icons/menu.svg" width="29" height="29" alt="Menu icon"> and clicking on choose columns <img src="../assets/svg/ag-grid-icons/columns.svg" width="29" height="29" alt="Choose Columns icon">.  In addition, you can remove columns that are checked.</li> <br/>
          <li>Grid data can be exported by clicking on the "Export Data" button.</li> <br/>
      </ul>
  `;
}
